import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { WorkflowContextService } from './workflow-context.service';
import { DataService } from './data.service';
import { Utilities } from './utilities';

// Import jQuery and SignalR (jQuery-based client for ASP.NET SignalR classic)
declare var $: any;
import 'signalr';

enum HubConnectionState {
  Disconnected = 'Disconnected',
  Connecting = 'Connecting',
  Connected = 'Connected',
  Reconnecting = 'Reconnecting'
}

@Injectable({
  providedIn: 'root'
})
export class SignalRService implements OnDestroy {
  private hubConnection: any = null; // jQuery SignalR hub proxy
  private connectionState$ = new BehaviorSubject<HubConnectionState>(
    HubConnectionState.Disconnected
  );
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 5000; // 5 seconds

  constructor(
    private context: WorkflowContextService,
    private dataService: DataService
  ) {}

  /**
   * Start the SignalR connection
   * @param hubName The name of the SignalR hub (e.g., 'MessagingHub')
   * @param accessTokenFactory Optional factory function to provide access token
   */
  async startConnection(
    hubName: string,
    accessTokenFactory?: () => string | Promise<string>
  ): Promise<void> {
    if (this.hubConnection) {
      console.warn('SignalR connection already exists');
      return;
    }

    const rootUrl = Utilities.getRootURL();

    // Debug logging
    //console.log('SignalR connecting to hub:', hubName);
    //console.log('Root URL:', rootUrl);

    try {
      // Get connection with custom URL
      const connection = $.hubConnection(rootUrl + '/signalr', { useDefaultPath: false });

      // Add access token if provided
      if (accessTokenFactory) {
        const token = typeof accessTokenFactory === 'function'
          ? await accessTokenFactory()
          : accessTokenFactory;

        connection.qs = { access_token: token };
        //console.log('Using access token:', token ? 'Token present' : 'No token');
      }

      // Create hub proxy
      this.hubConnection = connection.createHubProxy(hubName);

      // Enable logging
      connection.logging = true;

      // Set up connection state handlers
      this.setupConnectionHandlers(connection);

      // Start the connection
      this.connectionState$.next(HubConnectionState.Connecting);
      //console.log('Starting SignalR connection...');

      await connection.start();

      //console.log('SignalR connection established');
      //console.log('Connection ID:', connection.id);
      this.connectionState$.next(HubConnectionState.Connected);
      this.reconnectAttempts = 0;

    } catch (error) {
      console.error('Error starting SignalR connection:', error);
      this.connectionState$.next(HubConnectionState.Disconnected);
      this.hubConnection = null;
      throw error;
    }
  }

  /**
   * Stop the SignalR connection
   */
  async stopConnection(): Promise<void> {
    if (!this.hubConnection) {
      return;
    }

    try {
      const connection = this.hubConnection.connection;
      connection.stop();
      //console.log('SignalR connection stopped');
      this.connectionState$.next(HubConnectionState.Disconnected);
    } catch (error) {
      console.error('Error stopping SignalR connection:', error);
    } finally {
      this.hubConnection = null;
    }
  }

  /**
   * Register a handler for a specific hub method
   * @param methodName The name of the hub method to listen for
   * @param handler The callback function to handle the message
   */
  on<T = any>(methodName: string, handler: (...args: any[]) => void): void {
    if (!this.hubConnection) {
      console.error('Cannot register handler: SignalR connection not established');
      return;
    }

    this.hubConnection.on(methodName, handler);
    //console.log(`Registered handler for: ${methodName}`);
  }

  /**
   * Unregister a handler for a specific hub method
   * @param methodName The name of the hub method to stop listening for
   */
  off(methodName: string, handler?: (...args: any[]) => void): void {
    if (!this.hubConnection) {
      return;
    }

    this.hubConnection.off(methodName, handler);
    //console.log(`Unregistered handler for: ${methodName}`);
  }

  /**
   * Invoke a hub method and wait for a response
   * @param methodName The name of the hub method to invoke
   * @param args Arguments to pass to the hub method
   */
  async invoke<T = any>(methodName: string, ...args: any[]): Promise<T> {
    if (!this.hubConnection) {
      throw new Error('Cannot invoke method: SignalR connection not established');
    }

    const connection = this.hubConnection.connection;
    if (connection.state !== $.signalR.connectionState.connected) {
      throw new Error(`Cannot invoke method: Connection state is ${connection.state}`);
    }

    try {
      //console.log(`Invoking hub method: ${methodName}`, args);
      return await this.hubConnection.invoke(methodName, ...args);
    } catch (error) {
      console.error(`Error invoking ${methodName}:`, error);
      throw error;
    }
  }

  /**
   * Get the current connection state as an observable
   */
  getConnectionState(): Observable<HubConnectionState> {
    return this.connectionState$.asObservable();
  }

  /**
   * Get the current connection state
   */
  get connectionState(): HubConnectionState {
    return this.connectionState$.value;
  }

  /**
   * Check if the connection is currently connected
   */
  get isConnected(): boolean {
    return this.connectionState === HubConnectionState.Connected;
  }

  private setupConnectionHandlers(connection: any): void {
    connection.stateChanged((state: any) => {
      //console.log('SignalR state changed:', state);

      switch (state.newState) {
        case $.signalR.connectionState.connecting:
          this.connectionState$.next(HubConnectionState.Connecting);
          break;
        case $.signalR.connectionState.connected:
          this.connectionState$.next(HubConnectionState.Connected);
          break;
        case $.signalR.connectionState.reconnecting:
          this.connectionState$.next(HubConnectionState.Reconnecting);
          break;
        case $.signalR.connectionState.disconnected:
          this.connectionState$.next(HubConnectionState.Disconnected);
          break;
      }
    });

    connection.disconnected(() => {
      //console.log('SignalR connection disconnected');
      this.connectionState$.next(HubConnectionState.Disconnected);
      this.attemptReconnect();
    });

    connection.reconnecting(() => {
      //console.log('SignalR connection reconnecting...');
      this.connectionState$.next(HubConnectionState.Reconnecting);
    });

    connection.reconnected(() => {
      //console.log('SignalR connection reconnected');
      this.connectionState$.next(HubConnectionState.Connected);
      this.reconnectAttempts = 0;
    });

    connection.error((error: any) => {
      console.error('SignalR connection error:', error);
    });
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      //console.log('Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    this.connectionState$.next(HubConnectionState.Reconnecting);

    //console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

    setTimeout(async () => {
      if (!this.hubConnection) {
        return;
      }

      try {
        const connection = this.hubConnection.connection;
        await connection.start();
        //console.log('Reconnection successful');
        this.connectionState$.next(HubConnectionState.Connected);
        this.reconnectAttempts = 0;
      } catch (error) {
        console.error('Reconnection failed:', error);
        this.attemptReconnect();
      }
    }, this.reconnectDelay);
  }

  ngOnDestroy(): void {
    this.stopConnection();
  }
}
